import { useReportTraitOptions } from 'Src/helpers/service/admin/reportTraitOptions';
import * as React from 'react';
import propz from 'propz';
import { Form, Formik } from 'formik';
import { REPORT_TYPE_SERVER_TO_CLIENT_MAPPING } from '../../../../../../../consts/report';
import { SchoolStudent } from 'Src/models/schoolStudent';
import { ReportActions, ReportDateRange, ReportDescription, ReportSection, ReportToggle, ReportToggleList } from 'Src/views/GenericView/AdminView/TeamsAndEvents/Events/SchoolEventView/Reports/ReportFormUi';

const OPTIONS = {
  IS_TODAY: 'isToday',
  IS_YESTERDAY: 'isYesterday',
  IS_CURRENT_WEEK: 'isCurrentWeek',
  IS_PAST_WEEK: 'isPastWeek',
  IS_CUSTOM_INTERVAL: 'isCustomInterval'
};
interface Props { onCloseClick: () => void; onSubmit: (data: any, reportType: string) => void; student: SchoolStudent[]; activeSchoolId: string; }
const startOfDay = (date: Date) => new Date(new Date(date).setHours(0, 0, 1, 0));
const endOfDay = (date: Date) => new Date(new Date(date).setHours(23, 59, 59, 999));
const mondayFor = (date: Date) => { const result = new Date(date); const day = result.getDay(); result.setDate(result.getDate() - (day === 0 ? 6 : day - 1)); return result; };
const rangeForPeriod = (period: string) => {
  const today = new Date();
  if (period === OPTIONS.IS_YESTERDAY) { const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1); return { dateFrom: startOfDay(yesterday), dateTo: endOfDay(yesterday) }; }
  if (period === OPTIONS.IS_CURRENT_WEEK) { const monday = mondayFor(today); const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6); return { dateFrom: startOfDay(monday), dateTo: endOfDay(sunday) }; }
  if (period === OPTIONS.IS_PAST_WEEK) { const monday = mondayFor(today); monday.setDate(monday.getDate() - 7); const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6); return { dateFrom: startOfDay(monday), dateTo: endOfDay(sunday) }; }
  return { dateFrom: startOfDay(today), dateTo: endOfDay(today) };
};

export const StudentParticipationStatisticsByActivitiesReportForm = ({ onCloseClick, onSubmit, student, activeSchoolId }: Props) => {
  const isTraitAvailable = useReportTraitOptions();
  const todayRange = rangeForPeriod(OPTIONS.IS_TODAY);
  const initialValues = {
    options: OPTIONS.IS_TODAY,
    isYesterday: false,
    isCurrentWeek: false,
    isPastWeek: false,
    isCustomInterval: false,
    isPrintPP: false,
    isPrintSEN: false,
    isPrintFSM: false,
    isShowClubEvents: true,
    isGroupBySports: true,
    isGroupByEventTypes: false,
    isShowZero: false,
    isShowNonZero: true,
    dateFrom: todayRange.dateFrom,
    dateTo: todayRange.dateTo
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={values => {
      const valuesToTransfer = { ...values };
      propz.set(valuesToTransfer, [valuesToTransfer.options], true);
      delete valuesToTransfer.options;
      if (values.options !== OPTIONS.IS_CUSTOM_INTERVAL) {
        const range = rangeForPeriod(values.options);
        valuesToTransfer.dateFrom = range.dateFrom;
        valuesToTransfer.dateTo = range.dateTo;
      }
      onSubmit(valuesToTransfer, REPORT_TYPE_SERVER_TO_CLIENT_MAPPING.STUDENT_PARTICIPATION_STATISTICS_BY_ACTIVITIES);
    }}>
      {({ values, setFieldValue }) => (
        <Form className="eReportForm">
          <div className="eReportForm__scroll">
          <ReportDescription>See participation totals and breakdowns for selected students over a chosen period.</ReportDescription>
          <ReportSection number={1} title="Reporting period" hint="Choose the date range included in the report." required><ReportDateRange dateFrom={values.dateFrom} dateTo={values.dateTo} onChangeFrom={date => { setFieldValue('options', OPTIONS.IS_CUSTOM_INTERVAL); setFieldValue('dateFrom', date); if (values.dateTo < date) setFieldValue('dateTo', date); }} onChangeTo={date => { setFieldValue('options', OPTIONS.IS_CUSTOM_INTERVAL); setFieldValue('dateTo', date); }} /></ReportSection>
          <ReportSection number={2} title="Breakdown" hint="Choose how participation data is grouped and filtered."><ReportToggleList>
            <ReportToggle name="isShowClubEvents" label="Include club events" hint="Include statistics related to club events." value={values.isShowClubEvents} onChange={value => setFieldValue('isShowClubEvents', value)} />
            <ReportToggle name="isGroupBySports" label="Breakdown by activities/sports" hint="Show detailed statistics by activity or sport." value={values.isGroupBySports} onChange={value => setFieldValue('isGroupBySports', value)} />
            <ReportToggle name="isGroupByEventTypes" label="Breakdown by event types" hint="Show inter-school, intra-school, house and tournament breakdowns." value={values.isGroupByEventTypes} onChange={value => setFieldValue('isGroupByEventTypes', value)} />
            <ReportToggle name="isShowZero" label="Show non-participants" hint="Include students who did not participate in any events." value={values.isShowZero} onChange={value => setFieldValue('isShowZero', value)} />
            <ReportToggle name="isShowNonZero" label="Show participants" hint="Include students who participated in events." value={values.isShowNonZero} onChange={value => setFieldValue('isShowNonZero', value)} />
          </ReportToggleList></ReportSection>

          {((isTraitAvailable('isPrintPP') || isTraitAvailable('isPrintSEN') || isTraitAvailable('isPrintFSM'))) && <ReportSection number={3} title="Student information" hint="Choose additional student information to include."><ReportToggleList>
            {isTraitAvailable('isPrintPP') && <ReportToggle name="isPrintPP" label="Show PP" hint="Show data relating to students with PP." value={values.isPrintPP} onChange={value => setFieldValue('isPrintPP', value)} />}
            {isTraitAvailable('isPrintSEN') && <ReportToggle name="isPrintSEN" label="Show SEN" hint="Show data relating to students with SEN." value={values.isPrintSEN} onChange={value => setFieldValue('isPrintSEN', value)} />}
            {isTraitAvailable('isPrintFSM') && <ReportToggle name="isPrintFSM" label="Show FSM" hint="Show data relating to students with FSM." value={values.isPrintFSM} onChange={value => setFieldValue('isPrintFSM', value)} />}
          </ReportToggleList></ReportSection>}
          </div>
          <ReportActions onCancel={onCloseClick} disabled={!values.isShowZero && !values.isShowNonZero} />
        </Form>
      )}
    </Formik>
  );
};
